import { useState, type FormEvent } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import { Breadcrumb } from '@/components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/dbae77m9if4sq287d18g';

interface FormState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  message: string;
}

export default function ContactPage() {
  const [state, setState] = useState<FormState>({ status: 'idle', message: '' });
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('company_alt') || '').trim();
    if (honeypot) {
      setState({ status: 'success', message: '感谢你的留言，我们已经收到。' });
      form.reset();
      setMessageLength(0);
      return;
    }
    data.delete('company_alt');

    setState({ status: 'submitting', message: '' });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const failed =
        !response.ok ||
        parsed?.code !== 'OK' ||
        String(serverMessage || '').includes('spam');

      if (failed) {
        setState({
          status: 'error',
          message: serverMessage || '提交未能完成，请稍后重试，或检查填写内容后再次提交。',
        });
        return;
      }

      setState({ status: 'success', message: '感谢你的留言，我们已经收到，会尽快通过邮件回复。' });
      form.reset();
      setMessageLength(0);
    } catch {
      setState({ status: 'error', message: '网络连接异常，留言未能发送，请稍后重试。' });
    }
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950 px-4 md:px-10 pt-6 pb-10 md:pt-8 md:pb-14">
        <div className="[&_a]:text-background-400 [&_a:hover]:text-background-50 [&_span]:text-background-200 [&_i]:text-background-600">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
        </div>
        <div className="mt-5 md:mt-6 max-w-3xl">
          <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-accent-400 mb-3">
            <span className="w-4 h-px bg-accent-500"></span>
            Contact
          </span>
          <h1 className="font-heading text-2xl md:text-[40px] leading-tight font-semibold text-background-50 mb-4">
            联系我们
          </h1>
          <p className="text-sm md:text-[15px] leading-8 text-background-200">
            内容纠错、资料补充、版权沟通或合作咨询，都可以通过下面的表单留言。我们会在收到后逐一阅读。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-1.5">
                留言给我们
              </h2>
              <p className="text-xs text-foreground-500 mb-6">
                带 <span className="text-primary-600">*</span> 的为必填项，留言内容请控制在 500 字以内。
              </p>

              {state.status === 'success' ? (
                <div className="rounded-lg border border-accent-200 bg-accent-50 px-5 py-10 text-center">
                  <span className="w-14 h-14 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-2xl"></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">留言已提交</h3>
                  <p className="text-xs md:text-sm leading-7 text-foreground-600 mb-6">{state.message}</p>
                  <button
                    type="button"
                    onClick={() => setState({ status: 'idle', message: '' })}
                    className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-xs md:text-sm whitespace-nowrap hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    再写一条留言
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="请输入你的称呼"
                        className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        maxLength={80}
                        placeholder="name@example.com"
                        className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="topic" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言类型
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      defaultValue="内容纠错"
                      className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="内容纠错">内容纠错</option>
                      <option value="资料补充">资料补充</option>
                      <option value="版权沟通">版权沟通</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他建议">其他建议</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(e) => setMessageLength(e.target.value.length)}
                      placeholder="请描述你反馈的问题或合作意向，例如具体的剧集名称与页面位置。"
                      className="w-full px-3.5 py-3 rounded-md border border-background-300 bg-background-50 text-sm leading-7 text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-foreground-400">请勿填写个人敏感信息</span>
                      <span className={`text-[11px] ${messageLength > 480 ? 'text-primary-600' : 'text-foreground-400'}`}>
                        {messageLength} / 500
                      </span>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {state.status === 'error' && (
                    <p className="flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-3.5 py-2.5 text-xs leading-6 text-primary-800">
                      <i className="ri-error-warning-line mt-0.5"></i>
                      <span>{state.message}</span>
                    </p>
                  )}

                  <div className="pt-1 flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={state.status === 'submitting'}
                      className="h-11 px-6 rounded-md bg-primary-700 text-background-50 text-sm font-medium flex items-center gap-2 whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {state.status === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          提交留言
                        </>
                      )}
                    </button>
                    <span className="text-[11px] text-foreground-400">提交后会显示感谢提示，不会跳转页面</span>
                  </div>
                </form>
              )}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 overflow-hidden">
              <div className="w-full h-[200px] md:h-[230px] bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20quiet%20editorial%20desk%20scene%20with%20a%20notebook%20a%20pen%20and%20a%20cup%20of%20tea%20beside%20film%20photographs%2C%20soft%20amber%20window%20light%2C%20deep%20maroon%20and%20cream%20palette%2C%20calm%20inviting%20atmosphere%2C%20shallow%20depth%20of%20field&width=900&height=600&seq=ethics-contact-01&orientation=landscape"
                  alt="留言与沟通场景"
                  title="联系伦理片在线观看"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-5 md:p-6 bg-background-50">
                <h2 className="font-heading text-base font-semibold text-foreground-950 mb-3">其他联系方式</h2>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-mail-line', label: '内容与版权沟通', value: 'editor@ethics-cinema.example' },
                    { icon: 'ri-wechat-line', label: '合作咨询', value: 'ethics_cinema（示意）' },
                    { icon: 'ri-time-line', label: '回复时间', value: '工作日 10:00 - 18:00' },
                    { icon: 'ri-map-pin-line', label: '办公地址', value: '示例地址，仅作展示' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-md bg-background-100 border border-background-200 text-primary-700 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] text-foreground-500">{item.label}</p>
                        <p className="text-xs md:text-[13px] text-foreground-900 mt-0.5 break-all">{item.value}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 pt-4 border-t border-background-200 text-[11px] leading-6 text-foreground-400">
                  以上联系方式为示意信息，站内不提供播放与下载服务，请勿通过留言索取影片资源。
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-secondary-100/80 border border-secondary-200 px-5 py-4">
              <h3 className="text-sm font-medium text-secondary-900 mb-1.5 flex items-center gap-2">
                <i className="ri-information-line"></i>
                提交说明
              </h3>
              <p className="text-xs leading-6 text-secondary-900/80">
                本表单用于收集站内反馈与咨询，提交后的留言会存入站点表单记录，我们不会将信息用于其他用途。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}